import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, UserRound, X } from "lucide-react";

import type { GrantOpportunity, GrantOpportunityPayload } from "@/api/grant_opportunities";
import { listMunicipalityCollaborators } from "@/api/municipality_collaborators";
import { useCurrentUser } from "@/context/UserProvider";
import { userIsOutsideUser } from "@/lib/grantSuiteAccess";
import {
  addGrantOpportunityContributor,
  removeGrantOpportunityContributor,
} from "@/api/grant_opportunity_contributors";
import {
  addGrantOpportunityReviewer,
  removeGrantOpportunityReviewer,
} from "@/api/grant_opportunity_reviewers";
import { formatGrantCurrency } from "@/components/grant_suite/grantSuiteDisplay";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  grantOpportunityEventsQueryKey,
  invalidateGrantSuitePipeline,
} from "@/lib/grantSuiteQueryKeys";

type GrantOpportunityTeamFieldsProps = {
  municipalityId: number;
  opportunity: GrantOpportunity;
  onUpdate: (patch: Partial<GrantOpportunityPayload>) => void;
  updating: boolean;
};

const UNASSIGNED = "unassigned";
const ADD_REVIEWER = "add_reviewer";
const ADD_CONTRIBUTOR = "add_contributor";

export function GrantOpportunityTeamFields({
  municipalityId,
  opportunity,
  onUpdate,
  updating,
}: GrantOpportunityTeamFieldsProps) {
  const queryClient = useQueryClient();
  const reviewers = opportunity.reviewers ?? [];
  const contributors = opportunity.contributors ?? [];
  const reviewerUserIds = new Set(reviewers.map((reviewer) => reviewer.user_id));
  const contributorUserIds = new Set(contributors.map((contributor) => contributor.user_id));

  const currentUser = useCurrentUser();
  // Outside collaborators can't list team members (403) and can't assign
  // anyone anyway (ENG-1820).
  const outsideUser = userIsOutsideUser(currentUser);
  // Keyed on the department so a department-scoped opportunity does not read
  // the team-wide list out of the cache and offer people who cannot be
  // assigned to it (ENG-1824).
  const { data: collaborators = [], isLoading } = useQuery({
    queryKey: ["municipality_collaborators", municipalityId, opportunity.department_id ?? null],
    queryFn: () => listMunicipalityCollaborators(municipalityId, opportunity.department_id),
    enabled: !outsideUser,
  });

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ["grant_opportunities", municipalityId] });
    queryClient.invalidateQueries({
      queryKey: grantOpportunityEventsQueryKey(municipalityId, opportunity.id),
    });
    invalidateGrantSuitePipeline(queryClient, municipalityId);
  };

  const addReviewerMutation = useMutation({
    mutationFn: (userId: number) =>
      addGrantOpportunityReviewer(municipalityId, opportunity.id, userId),
    onSuccess: invalidate,
  });

  const removeReviewerMutation = useMutation({
    mutationFn: (userId: number) =>
      removeGrantOpportunityReviewer(municipalityId, opportunity.id, userId),
    onSuccess: invalidate,
  });

  const addContributorMutation = useMutation({
    mutationFn: (userId: number) =>
      addGrantOpportunityContributor(municipalityId, opportunity.id, userId),
    onSuccess: invalidate,
  });

  const removeContributorMutation = useMutation({
    mutationFn: (userId: number) =>
      removeGrantOpportunityContributor(municipalityId, opportunity.id, userId),
    onSuccess: invalidate,
  });

  // View-only variant (ENG-1877): people as text and badges, no selects, no
  // award-tracking inputs — those values already read out in the Details card.
  if (outsideUser) {
    return (
      <div className="space-y-4" data-testid="grant-opportunity-team-fields">
        <div className="space-y-1">
          <Label>Owner</Label>
          <p className="text-sm font-medium">{opportunity.assigned_to_name ?? "Unassigned"}</p>
        </div>
        <div className="space-y-1">
          <Label>Reviewers</Label>
          {reviewers.length > 0 ? (
            <div className="flex flex-wrap gap-2">
              {reviewers.map((reviewer) => (
                <Badge key={reviewer.id} variant="secondary" className="py-1 font-normal">
                  {reviewer.email}
                </Badge>
              ))}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">No reviewers yet.</p>
          )}
        </div>
        <div className="space-y-1">
          <Label>Contributors</Label>
          {contributors.length > 0 ? (
            <div className="flex flex-wrap gap-2">
              {contributors.map((contributor) => (
                <Badge key={contributor.id} variant="secondary" className="py-1 font-normal">
                  {contributor.email}
                </Badge>
              ))}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">No contributors yet.</p>
          )}
        </div>
      </div>
    );
  }

  const reviewerBusy = addReviewerMutation.isPending || removeReviewerMutation.isPending;
  const contributorBusy = addContributorMutation.isPending || removeContributorMutation.isPending;
  const availableCollaborators = collaborators.filter(
    (collaborator) => !reviewerUserIds.has(collaborator.id),
  );
  const availableContributorCollaborators = collaborators.filter(
    (collaborator) =>
      !contributorUserIds.has(collaborator.id) && collaborator.id !== opportunity.assigned_to_id,
  );

  return (
    <div className="space-y-5" data-testid="grant-opportunity-team-fields">
      <div className="space-y-2">
        <Label htmlFor="detail-owner">Owner</Label>
        <Select
          value={opportunity.assigned_to_id ? String(opportunity.assigned_to_id) : UNASSIGNED}
          onValueChange={(value) =>
            onUpdate({
              assigned_to_id: value === UNASSIGNED ? null : Number(value),
            })
          }
          disabled={updating || isLoading}
        >
          <SelectTrigger id="detail-owner">
            <SelectValue placeholder={isLoading ? "Loading team…" : "Unassigned"} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={UNASSIGNED}>Unassigned</SelectItem>
            {collaborators.map((collaborator) => (
              <SelectItem key={collaborator.id} value={String(collaborator.id)}>
                {collaborator.email}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <p className="text-xs text-muted-foreground">
          Team members with access to this municipality can be assigned as owner.
        </p>
      </div>

      <div className="space-y-2">
        <Label htmlFor="detail-reviewers">Reviewers</Label>
        <div className="space-y-3 rounded-lg border bg-muted/10 p-3">
          {reviewers.length > 0 ? (
            <div className="flex flex-wrap gap-2">
              {reviewers.map((reviewer) => (
                <Badge
                  key={reviewer.id}
                  variant="secondary"
                  className="gap-1.5 py-1 pl-2.5 pr-1 font-normal"
                >
                  {reviewer.email}
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-5 w-5 rounded-full"
                    disabled={reviewerBusy || updating}
                    aria-label={`Remove ${reviewer.email} as reviewer`}
                    onClick={() => removeReviewerMutation.mutate(reviewer.user_id)}
                  >
                    <X className="h-3 w-3" />
                  </Button>
                </Badge>
              ))}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">
              No reviewers yet. Add colleagues who should review this application.
            </p>
          )}

          <Select
            value={ADD_REVIEWER}
            onValueChange={(value) => {
              if (value === ADD_REVIEWER) return;
              addReviewerMutation.mutate(Number(value));
            }}
            disabled={reviewerBusy || updating || isLoading || availableCollaborators.length === 0}
          >
            <SelectTrigger id="detail-reviewers">
              <SelectValue
                placeholder={
                  isLoading
                    ? "Loading team…"
                    : availableCollaborators.length === 0
                      ? "All collaborators added"
                      : "Add reviewer…"
                }
              />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ADD_REVIEWER} disabled>
                Add reviewer…
              </SelectItem>
              {availableCollaborators.map((collaborator) => (
                <SelectItem key={collaborator.id} value={String(collaborator.id)}>
                  {collaborator.email}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {reviewerBusy ? (
            <p className="flex items-center gap-2 text-xs text-muted-foreground">
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
              Updating reviewers…
            </p>
          ) : null}
        </div>
        <p className="text-xs text-muted-foreground">
          Reviewers can follow progress in Activity and comment on the pursuit.
        </p>
      </div>

      <div className="space-y-2">
        <Label htmlFor="detail-contributors">Contributors</Label>
        <div className="space-y-3 rounded-lg border bg-muted/10 p-3">
          {contributors.length > 0 ? (
            <div className="flex flex-wrap gap-2">
              {contributors.map((contributor) => (
                <Badge
                  key={contributor.id}
                  variant="secondary"
                  className="gap-1.5 py-1 pl-2.5 pr-1 font-normal"
                >
                  {contributor.email}
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-5 w-5 rounded-full"
                    disabled={contributorBusy || updating}
                    aria-label={`Remove ${contributor.email} as contributor`}
                    onClick={() => removeContributorMutation.mutate(contributor.user_id)}
                  >
                    <X className="h-3 w-3" />
                  </Button>
                </Badge>
              ))}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">
              No contributors yet. Add colleagues who should edit milestones on this grant.
            </p>
          )}

          <Select
            value={ADD_CONTRIBUTOR}
            onValueChange={(value) => {
              if (value === ADD_CONTRIBUTOR) return;
              addContributorMutation.mutate(Number(value));
            }}
            disabled={
              contributorBusy ||
              updating ||
              isLoading ||
              availableContributorCollaborators.length === 0
            }
          >
            <SelectTrigger id="detail-contributors">
              <SelectValue
                placeholder={
                  isLoading
                    ? "Loading team…"
                    : availableContributorCollaborators.length === 0
                      ? "All collaborators added"
                      : "Add contributor…"
                }
              />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ADD_CONTRIBUTOR} disabled>
                Add contributor…
              </SelectItem>
              {availableContributorCollaborators.map((collaborator) => (
                <SelectItem key={collaborator.id} value={String(collaborator.id)}>
                  {collaborator.email}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {contributorBusy ? (
            <p className="flex items-center gap-2 text-xs text-muted-foreground">
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
              Updating contributors…
            </p>
          ) : null}
        </div>
        <p className="text-xs text-muted-foreground">
          Contributors can edit milestones and related task work on this grant.
        </p>
      </div>

      <div className="space-y-3 rounded-lg border bg-muted/10 p-3">
        <div className="flex items-center gap-2 text-sm font-medium">
          <UserRound className="h-4 w-4 text-muted-foreground" />
          Award tracking
        </div>
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="space-y-2">
            <Label htmlFor="detail-requested-amount">Requested amount</Label>
            <Input
              key={opportunity.id}
              id="detail-requested-amount"
              type="number"
              min="0"
              step="1000"
              placeholder="0"
              disabled={updating}
              defaultValue={opportunity.requested_amount ?? ""}
              onBlur={(event) => {
                const raw = event.target.value.trim();
                const next = raw === "" ? null : Number(raw);
                if (next === opportunity.requested_amount) return;
                if (next != null && Number.isNaN(next)) return;
                onUpdate({ requested_amount: next });
              }}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="detail-awarded-amount">Awarded amount</Label>
            <Input
              key={opportunity.id}
              id="detail-awarded-amount"
              type="number"
              min="0"
              step="1000"
              placeholder="0"
              disabled={updating}
              defaultValue={opportunity.awarded_amount ?? ""}
              onBlur={(event) => {
                const raw = event.target.value.trim();
                const next = raw === "" ? null : Number(raw);
                if (next === opportunity.awarded_amount) return;
                if (next != null && Number.isNaN(next)) return;
                onUpdate({ awarded_amount: next });
              }}
            />
          </div>
        </div>
        <div className="space-y-2">
          <Label htmlFor="detail-award-date">Award date</Label>
          <Input
            key={opportunity.id}
            id="detail-award-date"
            type="date"
            disabled={updating}
            defaultValue={opportunity.award_date ? opportunity.award_date.slice(0, 10) : ""}
            onBlur={(event) => {
              const next = event.target.value ? event.target.value : null;
              const current = opportunity.award_date ? opportunity.award_date.slice(0, 10) : null;
              if (next === current) return;
              onUpdate({ award_date: next });
            }}
          />
        </div>
        {(opportunity.requested_amount != null || opportunity.awarded_amount != null) && (
          <p className="text-xs text-muted-foreground">
            {opportunity.requested_amount != null
              ? `Requesting ${formatGrantCurrency(opportunity.requested_amount)}`
              : null}
            {opportunity.requested_amount != null && opportunity.awarded_amount != null ? " · " : null}
            {opportunity.awarded_amount != null
              ? `Awarded ${formatGrantCurrency(opportunity.awarded_amount)}`
              : null}
          </p>
        )}
      </div>
    </div>
  );
}
